import React, { useState, useCallback } from 'react';
import {
  Box,
  ClickAwayListener,
  InputAdornment,
  TextField,
  Tooltip,
  ToggleButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useSearch, SearchResult } from '../hooks/useSearch';
import { SearchResults } from './SearchResults';
import { useCategoryContext } from '../contexts/CategoryContext';
import { useCacheStore } from '../hooks/data/useCacheStore';

export const SearchBar: React.FC = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [regexMode, setRegexMode] = useState(false);

  const { search, hasNotes, regexInvalid } = useSearch();
  const { navigateToBookmark, selectNote } = useCategoryContext();
  const cacheStore = useCacheStore();

  const runSearch = useCallback(
    (value: string, useRegex: boolean) => {
      setResults(search(value, { regexMode: useRegex }));
    },
    [search],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setQuery(value);
      runSearch(value, regexMode);
      setShowResults(true);
    },
    [runSearch, regexMode],
  );

  const handleToggleRegex = useCallback(() => {
    const next = !regexMode;
    setRegexMode(next);
    if (query) runSearch(query, next);
  }, [regexMode, query, runSearch]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        setQuery('');
        setResults([]);
        setShowResults(false);
      }
    },
    [],
  );

  const handleClickAway = useCallback(() => {
    setShowResults(false);
  }, []);

  const handleSelect = useCallback(
    (result: SearchResult) => {
      if (result.kind === 'note') {
        selectNote(result.note, query);
      } else {
        const categories = cacheStore.getCategories();
        const category =
          categories?.find(
            (c) => c.category_id === result.bookmark.category_id,
          ) ?? null;
        // Sets category + highlight atomically (avoids race where the category
        // change would clear the highlight).
        navigateToBookmark(category, result.bookmark.bookmark_id);
      }
      setQuery('');
      setResults([]);
      setShowResults(false);
    },
    [cacheStore, navigateToBookmark, selectNote, query],
  );

  const handleClose = useCallback(() => {
    setShowResults(false);
  }, []);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <TextField
          variant="outlined"
          size="small"
          placeholder={
            regexMode ? 'Search (regex)…' : 'Search bookmarks & notes…'
          }
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          sx={{
            width: { xs: 200, sm: 300 },
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(255,255,255,0.15)',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
              </InputAdornment>
            ),
          }}
        />
        {hasNotes && (
          <Tooltip title="Regex search (notes)">
            <ToggleButton
              value="regex"
              selected={regexMode}
              onChange={handleToggleRegex}
              size="small"
              aria-label="Toggle regex search"
              sx={{
                color: 'rgba(255,255,255,0.7)',
                borderColor: 'rgba(255,255,255,0.3)',
                px: 1,
                py: 0.5,
                fontSize: '0.7rem',
                fontFamily: 'monospace',
                '&.Mui-selected': {
                  color: '#fff',
                  backgroundColor: 'rgba(255,255,255,0.25)',
                },
              }}
            >
              .*
            </ToggleButton>
          </Tooltip>
        )}
        {showResults && query && (
          <SearchResults
            results={results}
            query={query}
            onSelect={handleSelect}
            onClose={handleClose}
            regexInvalid={regexMode && regexInvalid}
          />
        )}
      </Box>
    </ClickAwayListener>
  );
};
